import type { AppInfoPagedGrid } from 'BrokerTsLib/src/dtos'
import type { AppInfoMessage, AppInfoMessagePagedQuery } from 'BrokerTsLib/src/dtos/User'
import type { Product } from 'BrokerTsLib/src/enums'
import type { GenericQueryOptions } from 'BrokerTsLib/src/hooks'
import { baseUrl, oneHourStaleTime } from 'BrokerTsLib/src/hooks'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '../Utils/apiClient'

const appInfoApiUrl = import.meta.env.VITE_APP_INFO_API
const url = `${baseUrl}/${appInfoApiUrl}`

const setReadAppInfoMessage = (data: { read: boolean; id: number }) =>
  apiClient.post(`${url}/SetReadAppInfoMessage`, data)

export const useSetReadAppInfoMessage = (product?: Product) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: setReadAppInfoMessage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['useGetUnreadMessages', product] })
    },
  })
}

export const useGetAllAppInfoMessages = () => {
  return useMutation({
    mutationFn: (data: AppInfoMessagePagedQuery) =>
      apiClient.post<AppInfoPagedGrid>(`${url}/GetAllAppInfoMessages`, data).then((res) => {
        res.data.items = res.data.items.map((item) => ({
          ...item,
          messageShow: (item.messageShow as any) === 1,
        }))
        return res
      }),
  })
}

export const useSaveAppInfoMessage = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (message: AppInfoMessage) => {
      const msg = { ...message } as any
      msg.messageShow = message.messageShow ? 1 : 0
      return apiClient.post<number>(`${url}/Save`, msg)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['useGetAppInfoMessage'] })
  })
}

export const useGetAppInfoMessage = (id: string) => {
  return useQuery({
    queryKey: ['useGetAppInfoMessage', id],
    queryFn: () =>
      apiClient.get<AppInfoMessage>(`${url}/Get?id=${id}`).then((res) => res.data),
  })
}

export const useGetAppInfoUnreadMessages = (
  product?: Product,
  options?: GenericQueryOptions<number>,
) => {
  return useQuery({
    queryKey: ['useGetUnreadMessages', product],
    queryFn: () => apiClient.get<number>(`${url}/GetUnreadMessages?product=${product}`),
    staleTime: oneHourStaleTime,
    ...options
  })
}
